import React, { useEffect, useState } from 'react';
import { X, MapPin, Clock, DollarSign, Activity, Hourglass, UtensilsCrossed, TreePine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Tour } from '../types/tours';
import { formatTime } from '../utils/dateUtils';

interface TourSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TourSelectionModal: React.FC<TourSelectionModalProps> = ({ isOpen, onClose }) => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      // Disable scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = '';
    }

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        // Fetch tours with their first carousel image
        const { data: toursData, error: toursError } = await supabase
          .from('tours')
          .select(`
            *,
            tour_images!inner (
              image_url,
              image_type,
              display_order
            )
          `)
          .eq('tour_images.image_type', 'carousel')
          .eq('tour_images.display_order', 0)
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (toursError) throw toursError;
        setTours(toursData || []);
      } catch (err) {
        console.error('Error fetching tours:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchTours();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 animate-in slide-in-from-bottom duration-300">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-[#2A382A]/10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl sm:text-2xl font-bold text-[#2A382A]">Quickly Book a Tour</h2>
            <button
              onClick={onClose}
              aria-label="Close tour selection modal"
              className="p-2 hover:bg-[#2A382A]/5 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[#2A382A]" />
            </button>
          </div>
          <p className="text-[#2A382A]/70 mt-2">
            Ready to secure your spot? Book directly from our available tours below. For detailed information about each tour, close this window and click "Explore All Our Adventures".
          </p>
          
          {/* Meeting Point Info */}
        </div>

        {/* Tours List */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-150px)] sm:max-h-[calc(80vh-200px)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-[#2A382A]/10 [&::-webkit-scrollbar-thumb]:bg-[#E85C0D] [&::-webkit-scrollbar-thumb]:rounded-full">
          {loading ? (
            <div className="text-center py-8 text-[#2A382A]/50">Loading tours...</div>
          ) : tours.length === 0 ? (
            <div className="text-center py-8 text-[#2A382A]/50">No tours available</div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {tours.map((tour) => {
                const thumbnailImage = tour.tour_images?.[0]?.image_url;
                // Calculate lowest price from non-null values
                const prices = [
                  tour.group_tour_price,
                  tour.reduced_group_price || null,
                  tour.private_price || null
                ].filter(price => price !== null && price > 0) as number[];

                const lowestPrice = Math.min(...prices);

                return (
                  <div
                    key={tour.id}
                    className="flex flex-col sm:flex-row gap-4 sm:gap-6 bg-[#1F2B1F] rounded-xl p-4 border border-white/10 transition-all duration-300 cursor-pointer hover:border-[#E85C0D]/50 hover:bg-[#2A382A] relative"
                    onClick={() => navigate(`/tours/${tour.slug}/book`)}>
                    {/* Image */}
                    <div className="w-full sm:w-16 aspect-[5/3] sm:aspect-square flex-shrink-0">
                      <div className="relative w-full h-full rounded-lg overflow-hidden">
                        {/* Blurred background */}
                        <div className="absolute inset-0">
                          <img
                            src={thumbnailImage}
                            alt=""
                            className="w-full h-full object-cover blur-sm scale-110 opacity-80"
                          />
                        </div>
                        {/* Main image */}
                        <img
                          src={thumbnailImage}
                          alt={tour.name}
                          className="absolute inset-0 w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      {/* Price Badge */}
                      <div className="absolute top-2 right-2 bg-[#953B0E] text-white px-3 py-1 rounded-full border-2 border-[#E85C0D] z-10">
                        From ${lowestPrice}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#E85C0D] transition-colors">
                        {tour.name}
                      </h3>
                      <p className="text-white/70 text-sm mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-none group-hover:text-white/90 transition-colors">
                        {tour.tour_card_description}
                      </p>

                      {/* Tour Details */}
                      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 text-sm group-hover:text-white/90 transition-colors">
                        <div className="flex items-center gap-2 text-white/70 group-hover:text-white/90 transition-colors">
                          <div className="w-6 h-6 bg-white rounded-[0.6rem] flex items-center justify-center">
                            <TreePine className="w-4 h-4 text-[#2A382A]" />
                          </div>
                          <span>{tour.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/70 group-hover:text-white/90 transition-colors">
                          <div className="w-6 h-6 bg-white rounded-[0.6rem] flex items-center justify-center">
                            <Clock className="w-4 h-4 text-[#2A382A]" />
                          </div>
                          <span>{formatTime(tour.departure).americanTime}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/70 group-hover:text-white/90 transition-colors">
                          <div className="w-6 h-6 bg-white rounded-[0.6rem] flex items-center justify-center">
                            <Activity className="w-4 h-4 text-[#2A382A]" />
                          </div>
                          <span className="capitalize">{tour.activity_level}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/70 group-hover:text-white/90 transition-colors">
                          <div className="w-6 h-6 bg-white rounded-[0.6rem] flex items-center justify-center">
                            <Hourglass className="w-4 h-4 text-[#2A382A]" />
                          </div>
                          <span>{tour.duration}</span>
                        </div>
                        <a
                          href={tour.meeting_point_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 text-white/70 group-hover:text-white/90 hover:text-[#E85C0D] transition-colors underline decoration-white/70 hover:decoration-[#E85C0D]"
                        >
                          <div className="w-6 h-6 bg-white rounded-[0.6rem] flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-[#2A382A]" />
                          </div>
                          <span>{tour.meeting_point}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TourSelectionModal;